import { Root } from '@studio/design-system/button';
import { Sel, STagNew } from '#~/stag/index.js';
import * as ContextMenu from '@studio/design-system/context-menu';
import { Mediator } from '@studio/design-system/input';
import { Ellipses } from '@studio/design-system/label';
import { Limits } from '@studio/models/control/preset';
import { useDoubleClick } from '@studio/react-utils/hooks';
import { toString } from '@tryiris-ai/js-types';
import { dequal } from 'dequal/lite';
import type { ReactElement } from 'react';
import { memo, useState } from 'react';
import { twMerge } from 'cn';
import { Menu } from './menu.jsx';
import type { Props } from './types.js';

/** @internal */
const Inner = ({
  className,
  group,
  handler,
  isDeletable,
  isSelected,
  ...props
}: Props): ReactElement => {
  const [isEditing, setEditing] = useState(false);

  const clicks = useDoubleClick({
    onDoubleClick: (): void => {
      setEditing(true);
    },

    onSingleClick: (): void => {
      handler.onSelect(group.id);
    }
  });

  const menuHandler = {
    onAdd(): void {
      handler.onAdd();
    },

    onRemove(): void {
      if (isDeletable) {
        handler.onRemove(group.id);
      }
    },

    onRename(): void {
      setEditing(true);
    }
  };

  return (
    <div className={twMerge('relative flex h-full items-center', className)} {...props}>
      {isEditing ? (
        <Mediator
          // Per playwright-stag-selectors-only (ai.knowledge/platform): editing keeps the group's stable identity.
          {...STagNew(Sel.MainRoomBottomTrayPresets, 'group.rename-input', group.id)}
          className='w-[125px]'
          focusOnMount
          maxLength={Limits.maxNameLength}
          onCancel={(): void => {
            setEditing(false);
          }}
          onCommit={(nextValue: string): void => {
            const nextName = toString(nextValue);

            if (nextName && nextName !== group.name) {
              handler.onRename(group.id, nextName);
            }

            setEditing(false);
          }}
          selectOnMount
          size='sm'
          value={group.name}
        />
      ) : (
        <ContextMenu.Root>
          <ContextMenu.Trigger>
            <Root
              aria-pressed={isSelected}
              {...STagNew(Sel.MainRoomBottomTrayPresets, 'group.select', group.id)}
              className={twMerge(
                'h-28 max-w-[100px] text-sm',
                isSelected
                  ? 'cursor-default font-demibold text-foreground'
                  : 'text-muted hover:text-foreground'
              )}
              {...clicks}
            >
              <Ellipses title={group.name}>{group.name}</Ellipses>
            </Root>

            <hr
              className={twMerge(
                'absolute inset-x-0 -bottom-px h-px transition-colors',
                isSelected ? 'border-primary' : 'border-transparent'
              )}
            />
          </ContextMenu.Trigger>

          <Menu handler={menuHandler} isDeletable={isDeletable} />
        </ContextMenu.Root>
      )}
    </div>
  );
};

/** `<Group>` component. */
export const Group = memo(Inner, dequal);
